(() => {
  if (window.icafeV7Ready) return;
  window.icafeV7Ready = true;

  const rootURL = () => new URL(window.Shopify?.routes?.root || '/', document.baseURI);
  const cartURL = path => new URL(path === 'js' ? 'cart.js' : `cart/${path}`, rootURL()).href;
  const requestJSON = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
      const data = await response.json();
      if (!response.ok) throw new Error('Cart request failed');
      return data;
    } finally { clearTimeout(timeout); }
  };
  const normalize = code => String(code).trim().toLocaleUpperCase('en-US');
  const activeCodes = cart => {
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes.filter(code => code.applicable !== false).map(code => code.code).filter(Boolean);
    }
    const applications = [...(cart.cart_level_discount_applications || [])];
    (cart.items || []).forEach(item => (item.line_level_discount_allocations || []).forEach(allocation => applications.push(allocation.discount_application)));
    return [...new Set(applications.filter(application => ['code', 'discount_code'].includes(application?.type)).map(application => application.title).filter(Boolean))];
  };
  let feedback = null;

  class IcafeCartDiscount extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="discount"]');
      this.button = this.querySelector('button[type="submit"]');
      this.message = this.querySelector('.ic-coupon__message');
      this.list = this.querySelector('.ic-coupon__codes');
      if (!this.form || !this.input || !this.button || !this.message || !this.list) return;
      this.applyLabel = this.button.textContent;
      this.form.addEventListener('submit', event => this.apply(event), { signal });
      this.input.addEventListener('input', () => {
        this.input.removeAttribute('aria-invalid');
        this.message.hidden = true;
      }, { signal });
      this.input.addEventListener('focus', () => this.loadCodes(), { signal, once: true });
      document.addEventListener('click', event => this.removeCode(event), { signal, capture: true });
      try { this.paintCodes(JSON.parse(this.querySelector('[data-coupon-codes]')?.textContent || '[]')); } catch { this.paintCodes([]); }
      if (feedback && Date.now() - feedback.time < 15000) {
        this.paintCodes(feedback.codes);
        this.showMessage(feedback.message, false);
        feedback = null;
      }
    }
    disconnectedCallback() { this.listeners?.abort(); }
    async loadCodes() {
      if (this.busy) return;
      try {
        const cart = await requestJSON(cartURL('js'));
        if (this.isConnected && !this.busy) this.paintCodes(activeCodes(cart));
      } catch { /* The submit action reports connection errors if needed. */ }
    }
    paintCodes(codes) {
      this.list.replaceChildren();
      for (const code of codes) {
        const item = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = code;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.icCouponRemove = code;
        remove.setAttribute('aria-label', `${this.dataset.remove}: ${code}`);
        remove.textContent = '×';
        item.append(label, remove);
        this.list.append(item);
      }
      this.list.hidden = codes.length === 0;
    }
    showMessage(text, error) {
      this.message.textContent = text;
      this.message.dataset.error = String(error);
      this.message.hidden = false;
      this.input.setAttribute('aria-invalid', String(error));
    }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.input.disabled = value;
      this.button.disabled = value;
      this.button.textContent = value ? this.dataset.applying : this.applyLabel;
      if (value) {
        this.checkout = document.querySelector('#CartDrawer button[name="checkout"]');
        this.checkoutWasDisabled = this.checkout?.disabled;
        if (this.checkout) this.checkout.disabled = true;
      } else if (this.checkout) this.checkout.disabled = !!this.checkoutWasDisabled;
    }
    updateDiscounts(codes) {
      return requestJSON(cartURL('update.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: codes.join(',') }) });
    }
    refresh(cart, message) {
      const codes = activeCodes(cart);
      this.paintCodes(codes);
      this.showMessage(message, false);
      feedback = { codes, message, time: Date.now() };
      document.dispatchEvent(new CustomEvent('cart:reload'));
    }
    async apply(event) {
      event.preventDefault();
      if (this.busy) return;
      const code = this.input.value.trim();
      if (!code) { this.showMessage(this.dataset.empty, true); this.input.focus(); return; }
      this.message.hidden = true;
      this.setBusy(true);
      try {
        const before = await requestJSON(cartURL('js'));
        const previous = activeCodes(before);
        const candidates = [...previous.filter(value => normalize(value) !== normalize(code)), code];
        const cart = await this.updateDiscounts(candidates);
        const applied = activeCodes(cart).map(normalize);
        const valid = applied.includes(normalize(code)) && previous.every(value => applied.includes(normalize(value)));
        if (!valid) {
          const restored = await this.updateDiscounts(previous);
          this.paintCodes(activeCodes(restored));
          this.showMessage(this.dataset.invalid, true);
          return;
        }
        this.input.value = '';
        this.refresh(cart, this.dataset.success);
      } catch { this.showMessage(this.dataset.error, true); }
      finally { this.setBusy(false); }
    }
    async removeCode(event) {
      const button = event.target.closest('[data-ic-coupon-remove],.hdt-discount-remove');
      if (!button || !this.closest('#CartDrawer')?.contains(button)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      const code = button.dataset.icCouponRemove || button.dataset.discountCode;
      if (!code) return;
      this.setBusy(true);
      try {
        const before = await requestJSON(cartURL('js'));
        const remaining = activeCodes(before).filter(value => normalize(value) !== normalize(code));
        const cart = await this.updateDiscounts(remaining);
        this.refresh(cart, this.dataset.removed);
      } catch { this.showMessage(this.dataset.error, true); }
      finally { this.setBusy(false); }
    }
  }
  if (!customElements.get('icafe-cart-discount')) customElements.define('icafe-cart-discount', IcafeCartDiscount);

  class IcafeRelatedProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.recommendationsReady === 'true' || this.loaded) return;
      if (!('IntersectionObserver' in window)) { this.load(); return; }
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '300px' });
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    async load() {
      if (this.loaded) return;
      this.loaded = true;
      try {
        const url = new URL(this.dataset.url, document.baseURI);
        if (url.origin !== new URL(document.baseURI).origin) return;
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector('icafe-related-products[data-recommendations-ready="true"] [data-related-track]');
        if (!source?.querySelector('.ic-atelier') || !this.isConnected) return;
        this.querySelector('[data-related-track]').replaceChildren(...source.childNodes);
        this.dataset.recommendationsReady = 'true';
      } catch { /* Curated, relevant cards stay available when recommendations are empty. */ }
    }
  }
  if (!customElements.get('icafe-related-products')) customElements.define('icafe-related-products', IcafeRelatedProducts);

  class IcafeBestSellers extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      if (!('IntersectionObserver' in window)) { this.load(); return; }
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '300px' });
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    async load() {
      if (this.loaded) return;
      this.loaded = true;
      try {
        const url = new URL(this.dataset.url, document.baseURI);
        if (url.origin !== new URL(document.baseURI).origin) return;
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const seen = new Set();
        const current = this.dataset.currentProduct;
        const cards = [...html.querySelectorAll('[data-ic-bestsellers-feed] .ic-atelier[data-ic-category="turkish"]')].filter(card => {
          const id = card.dataset.productId;
          if (id === current || seen.has(id) || !card.querySelector('button[name="add"]:not([disabled])')) return false;
          seen.add(id);
          return true;
        }).slice(0, Number(this.dataset.limit) || 4);
        if (!cards.length || !this.isConnected) return;
        this.querySelector('.ic-discovery__track').replaceChildren(...cards);
        this.dataset.feedLoaded = 'true';
      } catch { /* The verified initial bestseller order remains available. */ }
    }
  }
  if (!customElements.get('icafe-best-sellers')) customElements.define('icafe-best-sellers', IcafeBestSellers);

})();
